import { useCallback, useEffect, useRef, useState } from "react";
import {
  ClientIntakeViewV1Schema,
  type ClientIntakeEvidenceFileV1,
  type ClientIntakeSubmitRequestV1,
  type ClientIntakeViewV1,
  type PrimaryEvidenceType,
} from "@enrein/shared-contracts";
import {
  CLIENT_INTAKE_SUBMITTED_MESSAGE,
  toClientApiErrorMessage,
} from "@enrein/shared-contracts/display/tier1-client-display";
import type { ClientPortalApiClient } from "./api.js";

export function parseIntakeLines(value: string): Array<{ name: string }> {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((name) => ({ name }));
}

export function formatIntakeLines(items: Array<{ name: string }>): string {
  return items.map((item) => item.name).join("\n");
}

export function evidenceFilesFromFileList(files: FileList | null): ClientIntakeEvidenceFileV1[] {
  if (!files || files.length === 0) return [];
  return Array.from(files).map((file) => ({
    fileId: `file_${crypto.randomUUID()}`,
    fileName: file.name,
    contentType: file.type || "application/octet-stream",
    sizeBytes: file.size,
  }));
}

export function draftPrimaryEmail(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return undefined;
  return trimmed;
}

export type IntakeDraftSaveUiState = "idle" | "saving" | "saved" | "error";

/** Clear stale "Draft saved" / error when the user edits again so hard-reload waits on the latest save. */
export function nextDraftSaveStateAfterEdit(current: IntakeDraftSaveUiState): IntakeDraftSaveUiState {
  if (current === "saved" || current === "error") return "idle";
  return current;
}

/** Prefer live DOM field values so hard-reload flush/blur save what the user actually typed. */
export function readIntakeFieldValue(idCandidates: readonly string[], fallback: string): string {
  if (typeof document === "undefined") return fallback;
  for (const id of idCandidates) {
    const el = document.getElementById(id);
    if (el && "value" in el && typeof (el as HTMLInputElement).value === "string") {
      return (el as HTMLInputElement).value;
    }
  }
  return fallback;
}

export function buildIntakeDraftPayload(input: {
  businessName: string;
  primaryPhone: string;
  primaryEmail: string;
  description: string;
  servicesText: string;
  serviceAreasText: string;
  primaryEvidenceType: PrimaryEvidenceType | "";
  evidenceFiles: ClientIntakeEvidenceFileV1[];
}) {
  return {
    schemaVersion: "client-intake-draft-save.v1.0" as const,
    businessName: input.businessName.trim() || undefined,
    primaryPhone: input.primaryPhone.trim() || undefined,
    primaryEmail: draftPrimaryEmail(input.primaryEmail),
    description: input.description.trim() || undefined,
    services: parseIntakeLines(input.servicesText),
    serviceAreas: parseIntakeLines(input.serviceAreasText),
    primaryEvidenceType: input.primaryEvidenceType || undefined,
    evidenceFiles: input.evidenceFiles,
  };
}

export function useIntakeForm({
  api,
  clientLinkId,
  token,
  linkStatus,
  onStatus,
}: {
  readonly api: ClientPortalApiClient;
  readonly clientLinkId: string;
  readonly token: string;
  readonly linkStatus: string;
  readonly onStatus: (message: string | null) => void;
}) {
  const [view, setView] = useState<ClientIntakeViewV1 | null>(null);
  const [businessName, setBusinessName] = useState("");
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [primaryEmail, setPrimaryEmail] = useState("");
  const [description, setDescription] = useState("");
  const [servicesText, setServicesText] = useState("");
  const [serviceAreasText, setServiceAreasText] = useState("");
  const [primaryEvidenceType, setPrimaryEvidenceType] = useState<PrimaryEvidenceType | "">("");
  const [evidenceFiles, setEvidenceFiles] = useState<ClientIntakeEvidenceFileV1[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [draftSaveState, setDraftSaveState] = useState<IntakeDraftSaveUiState>("idle");
  const [draftSaveError, setDraftSaveError] = useState<string | null>(null);
  const hydratedRef = useRef(false);
  const skipAutosaveRef = useRef(true);
  const dirtyRef = useRef(false);
  const saveGenerationRef = useRef(0);
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const blurFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const valuesRef = useRef({
    businessName: "",
    primaryPhone: "",
    primaryEmail: "",
    description: "",
    servicesText: "",
    serviceAreasText: "",
    primaryEvidenceType: "" as PrimaryEvidenceType | "",
    evidenceFiles: [] as ClientIntakeEvidenceFileV1[],
  });
  valuesRef.current = {
    businessName,
    primaryPhone,
    primaryEmail,
    description,
    servicesText,
    serviceAreasText,
    primaryEvidenceType,
    evidenceFiles,
  };

  const readOnly = linkStatus === "INTAKE_SUBMITTED" || view?.status === "SUBMITTED";

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const next = ClientIntakeViewV1Schema.parse(await api.getIntake(clientLinkId, token));
        if (cancelled) return;
        setView(next);
        setBusinessName(next.businessName);
        setPrimaryPhone(next.primaryPhone ?? "");
        setPrimaryEmail(next.primaryEmail ?? "");
        setDescription(next.description ?? "");
        setServicesText(formatIntakeLines(next.services));
        setServiceAreasText(formatIntakeLines(next.serviceAreas));
        setPrimaryEvidenceType(next.primaryEvidenceType ?? "");
        setEvidenceFiles(next.evidenceFiles ?? []);
        setError(null);
        hydratedRef.current = true;
        skipAutosaveRef.current = true;
        dirtyRef.current = false;
      } catch (err) {
        if (!cancelled) {
          setView(null);
          setError(
            err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)),
          );
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [api, clientLinkId, token]);

  const persistDraft = useCallback(
    async (options?: { readonly keepalive?: boolean; readonly force?: boolean }) => {
      if (!hydratedRef.current || readOnly) return;
      const run = async () => {
        const stateSnapshot = valuesRef.current;
        const snapshot = {
          businessName: readIntakeFieldValue(
            ["v10-intake-business-name", "intake-business-name"],
            stateSnapshot.businessName,
          ),
          primaryPhone: readIntakeFieldValue(["v10-intake-phone", "intake-phone"], stateSnapshot.primaryPhone),
          primaryEmail: readIntakeFieldValue(["v10-intake-email", "intake-email"], stateSnapshot.primaryEmail),
          description: readIntakeFieldValue(
            ["v10-intake-description", "intake-description"],
            stateSnapshot.description,
          ),
          servicesText: readIntakeFieldValue(["v10-intake-services", "intake-services"], stateSnapshot.servicesText),
          serviceAreasText: readIntakeFieldValue(
            ["v10-intake-service-areas", "intake-service-areas"],
            stateSnapshot.serviceAreasText,
          ),
          primaryEvidenceType: readIntakeFieldValue(
            ["v10-intake-evidence", "intake-evidence"],
            stateSnapshot.primaryEvidenceType,
          ) as PrimaryEvidenceType | "",
          evidenceFiles: stateSnapshot.evidenceFiles,
        };
        if (snapshot.businessName.trim().length === 0) return;
        const generationAtStart = saveGenerationRef.current;
        // Keep controlled inputs aligned with DOM truth before any status setState re-render,
        // otherwise an in-progress multi-field edit can be wiped mid-fill.
        skipAutosaveRef.current = true;
        setBusinessName(snapshot.businessName);
        setPrimaryPhone(snapshot.primaryPhone);
        setPrimaryEmail(snapshot.primaryEmail);
        setDescription(snapshot.description);
        setServicesText(snapshot.servicesText);
        setServiceAreasText(snapshot.serviceAreasText);
        setPrimaryEvidenceType(snapshot.primaryEvidenceType);
        if (!options?.keepalive) {
          setDraftSaveState("saving");
          setDraftSaveError(null);
        }
        try {
          const result = await api.saveIntakeDraft(
            clientLinkId,
            token,
            buildIntakeDraftPayload(snapshot),
            options?.keepalive ? { keepalive: true } : undefined,
          );
          if (options?.keepalive) {
            if (result && saveGenerationRef.current === generationAtStart) {
              dirtyRef.current = false;
            }
            return;
          }
          if (!result) return;
          if (saveGenerationRef.current !== generationAtStart && !options?.force) {
            // Newer edits landed while this save was in flight — keep dirty for the next debounce.
            return;
          }
          dirtyRef.current = false;
          setView(result.intake);
          setBusinessName(result.intake.businessName);
          setPrimaryPhone(result.intake.primaryPhone ?? "");
          setPrimaryEmail(result.intake.primaryEmail ?? "");
          setDescription(result.intake.description ?? "");
          setServicesText(formatIntakeLines(result.intake.services));
          setServiceAreasText(formatIntakeLines(result.intake.serviceAreas));
          setPrimaryEvidenceType(result.intake.primaryEvidenceType ?? "");
          setEvidenceFiles(result.intake.evidenceFiles ?? []);
          skipAutosaveRef.current = true;
          setDraftSaveState("saved");
        } catch (err) {
          if (options?.keepalive) return;
          setDraftSaveState("error");
          setDraftSaveError(
            err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)),
          );
        }
      };
      const queued = saveChainRef.current.then(run, run);
      saveChainRef.current = queued.then(
        () => undefined,
        () => undefined,
      );
      await queued;
    },
    [api, clientLinkId, readOnly, token],
  );

  useEffect(() => {
    if (!hydratedRef.current || readOnly) return;
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }
    dirtyRef.current = true;
    saveGenerationRef.current += 1;
    setDraftSaveState((current) => nextDraftSaveStateAfterEdit(current));
    const handle = setTimeout(() => {
      void persistDraft();
    }, 800);
    return () => clearTimeout(handle);
  }, [
    businessName,
    description,
    evidenceFiles,
    persistDraft,
    primaryEmail,
    primaryEvidenceType,
    primaryPhone,
    readOnly,
    serviceAreasText,
    servicesText,
  ]);

  useEffect(() => {
    function flushPendingDraft() {
      if (!hydratedRef.current || readOnly) return;
      void persistDraft({ keepalive: true, force: true });
    }
    window.addEventListener("pagehide", flushPendingDraft);
    window.addEventListener("beforeunload", flushPendingDraft);
    return () => {
      window.removeEventListener("pagehide", flushPendingDraft);
      window.removeEventListener("beforeunload", flushPendingDraft);
    };
  }, [persistDraft, readOnly]);

  function attachEvidenceFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setEvidenceFiles((current) => [...current, ...evidenceFilesFromFileList(files)]);
  }

  async function submit() {
    if (readOnly || businessName.trim().length === 0) return;
    onStatus(null);
    setSubmitting(true);
    try {
      const payload: ClientIntakeSubmitRequestV1 = {
        schemaVersion: "client-intake-submit.v1.0",
        businessName: businessName.trim(),
        primaryPhone: primaryPhone.trim() || undefined,
        primaryEmail: primaryEmail.trim() || undefined,
        description: description.trim() || undefined,
        services: parseIntakeLines(servicesText),
        serviceAreas: parseIntakeLines(serviceAreasText),
        primaryEvidenceType: primaryEvidenceType || undefined,
        evidenceFiles,
      };
      const result = await api.submitIntake(clientLinkId, token, payload);
      setView(result.intake);
      onStatus(CLIENT_INTAKE_SUBMITTED_MESSAGE);
    } catch (err) {
      onStatus(
        err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return {
    view,
    error,
    readOnly,
    submitting,
    draftSaveState,
    draftSaveError,
    businessName,
    setBusinessName,
    primaryPhone,
    setPrimaryPhone,
    primaryEmail,
    setPrimaryEmail,
    description,
    setDescription,
    servicesText,
    setServicesText,
    serviceAreasText,
    setServiceAreasText,
    primaryEvidenceType,
    setPrimaryEvidenceType,
    evidenceFiles,
    attachEvidenceFiles,
    submit,
    saveDraftNow: () => persistDraft({ force: true }),
    flushDraftOnBlur: () => {
      if (!hydratedRef.current || readOnly) return;
      // Debounce blur flushes so tabbing across fields does not stampede concurrent saves.
      if (blurFlushTimerRef.current) clearTimeout(blurFlushTimerRef.current);
      blurFlushTimerRef.current = setTimeout(() => {
        blurFlushTimerRef.current = null;
        const active = typeof document !== "undefined" ? document.activeElement : null;
        const activeId = active && "id" in active ? String((active as HTMLElement).id || "") : "";
        if (
          activeId.startsWith("v10-intake-") ||
          activeId.startsWith("intake-")
        ) {
          // Focus moved to another intake field — wait for the later blur.
          return;
        }
        dirtyRef.current = true;
        saveGenerationRef.current += 1;
        void persistDraft({ force: true });
      }, 400);
    },
  };
}
